import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Check, MessageCircle, Mail, Phone, Truck, HelpCircle, ChevronRight, Loader2, Building2, MapPin } from "lucide-react";
import { BRAND, waUrl } from "@/lib/brand";
import { useLenis } from "@/hooks/useLenis";
import { useSeo } from "@/lib/seo";
import { graph, webPageLd } from "@/lib/schema";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/site/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const CARDS = [
  { icon: Phone, title: "Customer Support", desc: "Talk to our team for anything about Rudrima products.", value: BRAND.whatsappDisplay, href: waUrl("Hi Rudrima! I have a question about your products."), color: "#2E7D32", id: "contact-card-support" },
  { icon: Mail, title: "Email Support", desc: "Prefer email? Write to us and we'll reply within 24 hours.", value: BRAND.email, href: `mailto:${BRAND.email}`, color: "#D4A017", id: "contact-card-email" },
  { icon: MessageCircle, title: "WhatsApp Support", desc: "Fastest way to reach us — chat with us directly.", value: "Chat on WhatsApp", href: waUrl(), color: "#25D366", id: "contact-card-whatsapp" },
  { icon: Truck, title: "Order & Delivery Help", desc: "Questions about an order, shipping or delivery status.", value: "Share your Order Number", href: waUrl("Hi Rudrima! I need help with my order. Order Number: "), color: "#8D6E63", id: "contact-card-orders" },
];

const Field = ({ label, name, required, children }) => (
  <div>
    <label htmlFor={name} className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">
      {label}{required ? " *" : <span className="normal-case tracking-normal font-medium"> (optional)</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-4 py-3 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition";
const EMPTY = { name: "", email: "", phone: "", order_number: "", subject: "", message: "" };

export default function ContactPage() {
  useLenis();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const crumbs = [{ label: "Home", to: "/" }, { label: "Contact Us" }];

  useSeo({
    title: "Contact Rudrima — Customer Support, Orders & WhatsApp Help",
    description: "Contact Rudrima for help with A2 cow ghee or forest honey orders, shipping and delivery. Reach us on WhatsApp, email or send a message — we reply within 24 hours.",
    path: "/contact-us",
    jsonLd: graph(
      webPageLd({ path: "/contact-us", name: "Contact Rudrima", description: "Customer support, orders and delivery help for Rudrima.", type: "ContactPage" }),
      breadcrumbJsonLd(crumbs),
      {
        "@type": "Organization",
        "@id": "https://rudrima.com/#legal-entity",
        name: BRAND.company,
        brand: { "@type": "Brand", name: "Rudrima" },
        address: { "@type": "PostalAddress", streetAddress: "E-2/125, Vinamra Khand, Gomtinagar", addressLocality: "Lucknow", addressRegion: "Uttar Pradesh", postalCode: "226010", addressCountry: "IN" },
      },
    ),
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, product: form.subject });
      setSent(true);
      setForm(EMPTY);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <main data-testid="contact-page">
        <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-12 lg:pb-16 bg-hero-gradient grain overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
            <Breadcrumbs items={crumbs} className="mb-6" />
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="chip chip-gold mb-5"><Send className="w-3.5 h-3.5" /> Contact Rudrima</motion.div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#143C18] leading-[1.05] tracking-tight max-w-3xl" data-testid="contact-hero-title">
              {["We'd Love to", "Hear From You"].map((l, i) => (
                <span key={l} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                  <motion.span className={`block ${i === 1 ? "italic font-serif-alt text-[#2E7D32]" : ""}`} initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, ease, delay: 0.15 + i * 0.13 }}>{l}</motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }} className="mt-6 text-base sm:text-lg text-[#5D4037] max-w-2xl leading-relaxed" data-testid="contact-hero-sub">
              Have a question about our products, orders, shipping or anything else? Our team is here to help.
            </motion.p>
          </div>
        </section>

        <section className="py-14 sm:py-20 bg-[#FFF8E7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 grid sm:grid-cols-2 lg:grid-cols-1 gap-4">
              <motion.div
                data-testid="contact-card-office"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="sm:col-span-2 lg:col-span-1 relative overflow-hidden rounded-3xl bg-[#143C18] text-white p-6 shadow-[0_20px_40px_-24px_rgba(20,60,24,0.6)]"
              >
                <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
                <div className="relative flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#D4A017] flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-[#143C18]" /></div>
                  <address className="not-italic min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#E8BE4A] font-bold">Our Office</div>
                    <h2 className="font-display text-xl font-bold mt-1" data-testid="contact-company-name">{BRAND.company}</h2>
                    <div className="mt-3 text-[11px] uppercase tracking-widest text-white/60 font-bold">Registered Office</div>
                    <p className="mt-1 text-sm sm:text-[15px] text-white/90 leading-relaxed" data-testid="contact-company-address">
                      {BRAND.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
                    </p>
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-white/70"><MapPin className="w-3.5 h-3.5 text-[#E8BE4A]" /> Lucknow, India</div>
                  </address>
                </div>
              </motion.div>
              {CARDS.map((c, i) => (
                <motion.a
                  key={c.title}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={c.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  className="group flex items-start gap-4 p-5 rounded-3xl bg-white border border-[#8D6E63]/12 hover:border-[#2E7D32]/40 hover:-translate-y-1 transition-all duration-400 shadow-[0_10px_30px_-24px_rgba(20,60,24,0.4)]"
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" style={{ background: c.color }}>
                    <c.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-display text-xl font-bold text-[#143C18]">{c.title}</h2>
                    <p className="text-sm text-[#5D4037] mt-1">{c.desc}</p>
                    <div className="mt-2 text-sm font-semibold text-[#2E7D32] inline-flex items-center gap-1 break-all">{c.value} <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></div>
                  </div>
                </motion.a>
              ))}
            </div>

            <motion.form
              onSubmit={onSubmit}
              data-testid="contact-page-form"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="lg:col-span-7 card-premium !transform-none p-6 sm:p-8 lg:p-10"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#143C18] mb-6">Send us a message</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" name="name" required><input id="name" name="name" value={form.name} onChange={onChange} required data-testid="contact-page-name" className={inputCls} placeholder="Your full name" autoComplete="name" /></Field>
                <Field label="Email" name="email" required><input id="email" type="email" name="email" value={form.email} onChange={onChange} required data-testid="contact-page-email" className={inputCls} placeholder="you@example.com" autoComplete="email" /></Field>
                <Field label="Phone Number" name="phone" required><input id="phone" type="tel" name="phone" value={form.phone} onChange={onChange} required data-testid="contact-page-phone" className={inputCls} placeholder="+91 98xxxxxxxx" autoComplete="tel" /></Field>
                <Field label="Order Number" name="order_number"><input id="order_number" name="order_number" value={form.order_number} onChange={onChange} data-testid="contact-page-order" className={inputCls} placeholder="e.g. #1024" /></Field>
              </div>
              <div className="mt-4">
                <Field label="Subject" name="subject" required>
                  <select id="subject" name="subject" value={form.subject} onChange={onChange} required data-testid="contact-page-subject" className={inputCls}>
                    <option value="">Select a subject</option>
                    <option>Product Question</option>
                    <option>Order Status</option>
                    <option>Shipping & Delivery</option>
                    <option>Return / Refund</option>
                    <option>Bulk / Wholesale</option>
                    <option>Other</option>
                  </select>
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Message" name="message" required><textarea id="message" name="message" value={form.message} onChange={onChange} required rows={5} data-testid="contact-page-message" className={`${inputCls} resize-none`} placeholder="How can we help you?" /></Field>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 justify-between">
                <p className="text-xs text-[#8D6E63]">By submitting, you agree to be contacted by Rudrima. See our <Link to="/privacy-policy" className="underline hover:text-[#2E7D32]">Privacy Policy</Link>.</p>
                <button type="submit" disabled={submitting} data-testid="contact-page-submit" className="btn-primary w-full sm:w-auto justify-center !py-3.5 !px-8 text-base whitespace-nowrap shrink-0 disabled:opacity-60">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
              </div>
              {sent && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="contact-page-success" className="mt-5 p-4 rounded-xl bg-[#2E7D32]/10 border border-[#2E7D32]/30 text-[#1F5D25] text-sm font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" /> Thank you! Your message has been sent. We'll respond via WhatsApp or email within 24 hours.
                </motion.div>
              )}
            </motion.form>
          </div>
        </section>

        <section className="py-12 sm:py-16 lg:py-20 bg-[#143C18] text-white relative overflow-hidden" data-testid="contact-faq-cta">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0"><HelpCircle className="w-6 h-6 text-[#E8BE4A]" /></div>
              <div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold">Looking for quick answers?</h2>
                <p className="mt-2 text-white/75">Storage, delivery, ordering and more — answered in our FAQ.</p>
              </div>
            </div>
            <Link to="/#faq" data-testid="contact-faq-btn" className="btn-gold shrink-0">Visit the FAQ <ChevronRight className="w-4 h-4" /></Link>
          </div>
        </section>
      </main>
      <Footer bottomPad="pb-10" />
      <FloatingWhatsApp />
    </>
  );
}
